/*
 * Copyright (c) 2026 Bluemate B.V.
 */

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import InfoBanner from '../misc/infoBanner';

/** `install` = Chromium captured `beforeinstallprompt` and can install in one tap.
 *  `open-in-chrome` = Android browser without install support, hand it to Chrome. */
export type InstallPromptVariant = 'install' | 'open-in-chrome';

interface Props {
    variant: InstallPromptVariant;
    onAction: MouseEventHandler;
    actionLoading?: boolean;
    onClose?: MouseEventHandler;
}

export default function AndroidInstallPromptBanner(props: Props) {
    const {variant, onAction, actionLoading, onClose} = props;
    const {t} = useTranslation();

    if (variant === 'install')
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.installBluemateTitle', 'Installeer Bluemate als app')}
                persistDismiss={false}
                primaryBannerButton
                actionLabel={t('pwa.install', 'Installeer')}
                onAction={onAction}
                actionLoading={actionLoading}
                onClose={onClose}
            >
                {t(
                    'pwa.installBluemate',
                    "Voeg Bluemate toe aan je bureaublad of startscherm. Als geïnstalleerde app start Bluemate sneller en kun je push-meldingen ontvangen over koopblok-wissels, lage voorraad en nieuwe reviews. Tik of klik op 'Installeer' om de installatiedialoog van je browser te openen."
                )}
            </InfoBanner>
        );

    return (
        <InfoBanner
            variant='info'
            title={t('pwa.androidIncompatibleInstallTitle', 'Installeer Bluemate op je Android-toestel')}
            persistDismiss={false}
            primaryBannerButton
            actionLabel={t('pwa.openInChrome', 'In Chrome openen')}
            onAction={onAction}
            onClose={onClose}
        >
            {t(
                'pwa.androidIncompatibleInstallHint',
                'Je huidige browser ondersteunt geen één-klik installatie van Bluemate als app. Tik op de knop hieronder om Bluemate te openen in Google Chrome — daar kun je de app met één tik installeren. Heb je Chrome nog niet? Dan opent de Play Store om Chrome te installeren.'
            )}
        </InfoBanner>
    );
}
